import dayjs from 'dayjs';

import {AdminEbayAccount} from '../../../gen/api/models';

/** Warn this many days before the stored sign-in expires. */
export const EXPIRY_WARNING_DAYS = 30;

/** How often pages outside the eBay page re-read the account for the warning. */
export const SIGN_IN_CHECK_MS = 10 * 60_000;

export interface SignInWarning {
    type: 'error' | 'warning';
    message: string;
    description: string;
}

/** Whole days until the stored sign-in expires; 0 or less when it has. */
export function signInDaysLeft(account: AdminEbayAccount): number {
    return dayjs(account.refreshTokenExpiresAt).diff(dayjs(), 'day');
}

/** What to tell the admins about the stored sign-in: refused, expired or expiring soon; undefined while it is fine. */
export function signInWarning(account: AdminEbayAccount): SignInWarning | undefined {
    const consequence =
        'Publishing, updating and ending listings and importing eBay orders stop until it is reconnected.';
    if (account.reconnectRequiredAt) {
        return {
            type: 'error',
            message: 'eBay no longer accepts the stored sign-in',
            description: `eBay refused it ${dayjs(account.reconnectRequiredAt).format('MM/DD/YYYY HH:mm')} (revoked or expired, e.g. after a password change on eBay). ${consequence}`,
        };
    }
    const daysLeft = signInDaysLeft(account);
    if (daysLeft <= 0) {
        return {
            type: 'error',
            message: 'The eBay sign-in has expired',
            description: `It expired ${dayjs(account.refreshTokenExpiresAt).format('MM/DD/YYYY')}. ${consequence}`,
        };
    }
    if (daysLeft < EXPIRY_WARNING_DAYS) {
        return {
            type: 'warning',
            message: `The eBay sign-in expires in ${daysLeft} day${daysLeft === 1 ? '' : 's'}`,
            description: `It is valid until ${dayjs(account.refreshTokenExpiresAt).format('MM/DD/YYYY')}. Reconnect the account before then: eBay gives a new sign-in for 18 months.`,
        };
    }
    return undefined;
}
